import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Leistungen", id: "services" },
  { label: "Projekte", id: "projects" },
  { label: "Warum monoton.", id: "about" },
  { label: "Kontakt", id: "contact" },
];

const goTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: scrolled ? "rgba(5,5,6,0.62)" : "transparent",
        backdropFilter: scrolled ? "blur(24px) saturate(140%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(24px) saturate(140%)" : "none",
        borderBottom: scrolled ? "1px solid rgba(244,152,0,0.10)" : "1px solid transparent",
        transition:
          "background 320ms var(--ease-premium), border-color 320ms var(--ease-premium), backdrop-filter 320ms",
      }}
    >
      <div className="h-[88px] px-5 sm:px-7 lg:px-10 flex items-center gap-6 lg:gap-10 relative">
        {/* Big MONOTON. wordmark — horizontal gradient, larger */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group relative shrink-0"
          data-testid="header-wordmark"
          aria-label="Zum Anfang"
        >
          <span
            className="font-display select-none block"
            style={{
              fontSize: "clamp(32px, 3.4vw, 54px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1,
              backgroundImage:
                "linear-gradient(90deg, #F49800 0%, #F97316 45%, #E71681 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textTransform: "uppercase",
              filter: "drop-shadow(0 0 28px rgba(244,152,0,0.32))",
            }}
          >
            MONOTON.
          </span>
        </button>

        {/* Center nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-center">
          {NAV.map((n) => (
            <button
              key={n.id}
              className="nav-link"
              onClick={() => goTo(n.id)}
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4 lg:gap-6 shrink-0 ml-auto md:ml-0">
          <button
            onClick={() => goTo("contact")}
            className="hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-display font-medium transition-all"
            style={{
              background: "var(--offwhite)",
              color: "var(--ink)",
              boxShadow: "0 0 30px rgba(244,152,0,0.12)",
            }}
            data-testid="header-cta"
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 0 40px rgba(244,152,0,0.5)")}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 0 30px rgba(244,152,0,0.12)")}
          >
            Projekt anfragen
          </button>
          <button
            className="md:hidden p-2"
            aria-label="Menü"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
          >
            <Menu className="text-offwhite" size={22} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              className="absolute inset-0"
              onClick={() => setOpen(false)}
              style={{ background: "rgba(5,5,6,0.7)", backdropFilter: "blur(20px)" }}
            />
            <motion.aside
              className="absolute top-0 right-0 h-full w-[80%] max-w-sm p-8 flex flex-col gap-8"
              style={{
                background: "rgba(5,5,6,0.92)",
                borderLeft: "1px solid rgba(244,152,0,0.15)",
              }}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="font-display"
                  style={{
                    fontWeight: 800,
                    fontSize: 26,
                    letterSpacing: "-0.04em",
                    backgroundImage: "linear-gradient(90deg,#F49800,#E71681)",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    textTransform: "uppercase",
                  }}
                >
                  MONOTON.
                </span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Schließen"
                  data-testid="mobile-menu-close"
                >
                  <X className="text-offwhite" size={22} />
                </button>
              </div>
              <nav className="flex flex-col gap-5 mt-6">
                {NAV.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => {
                      goTo(n.id);
                      setOpen(false);
                    }}
                    className="text-left font-display text-2xl text-offwhite font-medium"
                    data-testid={`mobile-nav-${n.id}`}
                  >
                    {n.label}
                  </button>
                ))}
              </nav>
              <button
                onClick={() => {
                  goTo("contact");
                  setOpen(false);
                }}
                className="btn-primary mt-auto justify-center"
                data-testid="mobile-cta"
              >
                Projekt anfragen →
              </button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
